import { useState, useEffect } from 'react';
import {
  BlockStack,
  InlineStack,
  TextField,
  Button,
  Text,
  Box,
  Divider,
  Banner,
} from '@shopify/polaris';
import {
  inquiryService,
  type InquiryItem,
  type Quote,
  type CreateQuoteInput,
} from '../../services/inquiry.service';

interface QuoteLineItem {
  productId: string;
  productName: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface QuoteBuilderProps {
  inquiryId: string;
  items: InquiryItem[];
  existingQuote: Quote | null;
  onSave: () => void;
  onCancel: () => void;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function QuoteBuilder({
  inquiryId,
  items,
  existingQuote,
  onSave,
  onCancel,
}: QuoteBuilderProps) {
  const [lineItems, setLineItems] = useState<QuoteLineItem[]>([]);
  const [validityDays, setValidityDays] = useState('30');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Initialize line items from inquiry items or existing quote
  useEffect(() => {
    if (existingQuote) {
      // Load from existing quote
      setLineItems(
        existingQuote.items.map((item) => ({
          productId: item.product.id,
          productName: item.product.name,
          sku: item.product.sku,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          total: item.total,
        }))
      );
    } else {
      // Initialize from inquiry items
      setLineItems(
        items.map((item) => ({
          productId: item.product.id,
          productName: item.product.name,
          sku: item.product.sku,
          quantity: item.quantity,
          unitPrice: 0,
          total: 0,
        }))
      );
    }
  }, [items, existingQuote]);

  const updateLineItem = (
    index: number,
    field: 'quantity' | 'unitPrice',
    value: string
  ) => {
    const numValue = parseFloat(value) || 0;
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: numValue,
        total:
          field === 'quantity'
            ? numValue * updated[index].unitPrice
            : updated[index].quantity * numValue,
      };
      return updated;
    });
  };

  const grandTotal = lineItems.reduce((sum, item) => sum + item.total, 0);

  const handleSave = async () => {
    // Validate
    const invalidItems = lineItems.filter(
      (item) => item.quantity <= 0 || item.unitPrice <= 0
    );
    if (invalidItems.length > 0) {
      setError('All items must have quantity and unit price greater than 0');
      return;
    }

    const validity = parseInt(validityDays) || 30;
    if (validity < 1 || validity > 365) {
      setError('Validity must be between 1 and 365 days');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const data: CreateQuoteInput = {
        items: lineItems.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        })),
        validityDays: validity,
      };

      await inquiryService.createQuote(inquiryId, data);
      onSave();
    } catch (err) {
      setError('Failed to save quote');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BlockStack gap="400">
      {error && (
        <Banner tone="critical" onDismiss={() => setError('')}>
          {error}
        </Banner>
      )}

      <Text as="p" tone="subdued">
        Set the unit price for each item to generate a quote. The quote will be
        created as a draft and can be sent to the customer after review.
      </Text>

      {/* Line Items */}
      <BlockStack gap="300">
        {/* Header */}
        <Box
          padding="200"
          background="bg-surface-secondary"
          borderRadius="200"
        >
          <InlineStack align="space-between">
            <Box minWidth="40%">
              <Text as="p" variant="bodySm" fontWeight="semibold">
                Product
              </Text>
            </Box>
            <Box minWidth="15%">
              <Text as="p" variant="bodySm" fontWeight="semibold">
                Quantity
              </Text>
            </Box>
            <Box minWidth="20%">
              <Text as="p" variant="bodySm" fontWeight="semibold">
                Unit Price
              </Text>
            </Box>
            <Box minWidth="15%">
              <Text as="p" variant="bodySm" fontWeight="semibold" alignment="end">
                Total
              </Text>
            </Box>
          </InlineStack>
        </Box>

        {/* Items */}
        {lineItems.map((item, index) => (
          <Box
            key={item.productId}
            padding="300"
            borderColor="border"
            borderWidth="025"
            borderRadius="200"
          >
            <InlineStack align="space-between" blockAlign="center">
              <Box minWidth="40%">
                <BlockStack gap="050">
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    {item.productName}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    SKU: {item.sku}
                  </Text>
                </BlockStack>
              </Box>
              <Box minWidth="15%">
                <TextField
                  label=""
                  labelHidden
                  type="number"
                  value={item.quantity.toString()}
                  onChange={(value) => updateLineItem(index, 'quantity', value)}
                  min={1}
                  autoComplete="off"
                />
              </Box>
              <Box minWidth="20%">
                <TextField
                  label=""
                  labelHidden
                  type="number"
                  value={item.unitPrice.toString()}
                  onChange={(value) => updateLineItem(index, 'unitPrice', value)}
                  prefix="₹"
                  min={0}
                  autoComplete="off"
                />
              </Box>
              <Box minWidth="15%">
                <Text as="p" variant="bodyMd" alignment="end">
                  {formatCurrency(item.total)}
                </Text>
              </Box>
            </InlineStack>
          </Box>
        ))}
      </BlockStack>

      <Divider />

      {/* Total */}
      <InlineStack align="space-between">
        <Text as="p" variant="headingMd">Grand Total</Text>
        <Text as="p" variant="headingLg" fontWeight="bold">
          {formatCurrency(grandTotal)}
        </Text>
      </InlineStack>

      <Divider />

      {/* Validity */}
      <TextField
        label="Quote Validity (days)"
        type="number"
        value={validityDays}
        onChange={setValidityDays}
        helpText="Number of days the quote will be valid from the date it's sent"
        min={1}
        max={365}
        autoComplete="off"
      />

      {/* Actions */}
      <InlineStack gap="300" align="end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={saving}
          disabled={grandTotal <= 0}
        >
          {existingQuote ? 'Update Quote' : 'Create Quote'}
        </Button>
      </InlineStack>
    </BlockStack>
  );
}
